import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Copy, Check, Sparkles, Linkedin, ChevronLeft, Search } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";

export const LinkedInOptimiser = ({ onBack }: { onBack?: () => void }) => {
  const { user, session } = useAuth();
  const { profile, refetchProfile } = useProfile();

  const [headline, setHeadline] = useState("");
  const [about, setAbout] = useState("");
  const [experience, setExperience] = useState("");
  const [targetRole, setTargetRole] = useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ HEADLINE: string; ABOUT: string; EXPERIENCE: string } | null>(null);

  const [copiedHeadline, setCopiedHeadline] = useState(false);
  const [copiedAbout, setCopiedAbout] = useState(false);
  const [copiedExperience, setCopiedExperience] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  // Search Helper State
  const [activeTab, setActiveTab] = useState<"optimise" | "search">("optimise");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [timeFrame, setTimeFrame] = useState("r86400"); // Default to past 24 hours

  const handleSearchClick = () => {
    if (!searchKeyword.trim()) {
      toast.error("Please enter a keyword to search");
      return;
    }
    const url = `https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(searchKeyword)}&location=${encodeURIComponent(searchLocation)}&f_TPR=${timeFrame}&refresh=true`;
    window.open(url, '_blank');
  };

  // Load latest optimisation on mount
  useEffect(() => {
    const loadLatest = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("linkedin_optimisations")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (data) {
        setHeadline(data.headline_input || "");
        setAbout(data.about_input || "");
        setExperience(data.experience_input || "");
        setTargetRole(data.target_role || "");
        
        if (data.optimised_headline || data.optimised_about || data.optimised_experience) {
          setResult({
            HEADLINE: data.optimised_headline || "",
            ABOUT: data.optimised_about || "",
            EXPERIENCE: data.optimised_experience || "",
          });
        }
      }
    };
    
    loadLatest();
  }, [user]);

  const handleOptimise = async () => {
    if (!headline && !about && !experience) {
      toast.error("Please provide at least one section to optimise.");
      return;
    }

    if (!user || !session) {
      toast.error("You must be logged in to use this feature.");
      return;
    }

    if (profile && profile.credits_used >= profile.credits_total) {
      toast.error("You've used all your credits. Please upgrade your plan.");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('optimise-linkedin', {
        body: {
          headline,
          about,
          experience,
          targetRole,
        }
      });

      if (error) {
        throw new Error(error.message || "Failed to optimise profile.");
      }

      setResult(data);
      toast.success("Profile optimised successfully!");

      // Save to database
      await supabase.from("linkedin_optimisations").insert({
        user_id: user.id,
        headline_input: headline,
        about_input: about,
        experience_input: experience,
        target_role: targetRole,
        optimised_headline: data.HEADLINE || "",
        optimised_about: data.ABOUT || "",
        optimised_experience: data.EXPERIENCE || "",
      });

      refetchProfile();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, setCopied: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyAll = () => {
    if (!result) return;
    const fullText = `HEADLINE\n${result.HEADLINE}\n\nABOUT\n${result.ABOUT}\n\nEXPERIENCE\n${result.EXPERIENCE}`;
    copyToClipboard(fullText, setCopiedAll);
  };

  return (
    <div className="flex-1 overflow-y-auto p-12 custom-scrollbar bg-background/50 h-full">
      <div className="max-w-6xl mx-auto space-y-8">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onBack}
          className="text-zinc-400 hover:text-white -ml-2"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#0a66c2]/10 border border-[#0a66c2]/20 flex items-center justify-center">
            <Linkedin className="w-6 h-6 text-[#0a66c2]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">LinkedIn Toolkit</h2>
            <p className="text-muted-foreground text-sm mt-1">Recruiter search tools and profile optimiser all in one place.</p>
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-border gap-6 mb-6">
          <button 
            onClick={() => setActiveTab("optimise")}
            className={`pb-3 text-sm font-semibold transition-colors relative ${activeTab === 'optimise' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Profile Optimiser
            {activeTab === 'optimise' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-full" />}
          </button>
          <button 
            onClick={() => setActiveTab("search")}
            className={`pb-3 text-sm font-semibold transition-colors relative ${activeTab === 'search' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Job Search Helper
            {activeTab === 'search' && <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-full" />}
          </button>
        </div>

        {activeTab === "optimise" ? (
          <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Input */}
          <div className="space-y-6 flex flex-col h-full">
            <div className="bg-card rounded-2xl border border-border shadow-lg p-6 flex-1 space-y-5">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Target Job Title or Industry</label>
                <input
                  type="text"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  placeholder="e.g. Senior Performance Marketing Manager"
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Your Current Headline</label>
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="e.g. Marketing Professional"
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground"
                />
              </div>

              <div className="space-y-2 flex-1 flex flex-col">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Your Current About Section</label>
                <textarea
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  placeholder="Paste your current about summary here..."
                  className="w-full flex-1 min-h-[120px] bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground resize-none"
                />
              </div>

              <div className="space-y-2 flex-1 flex flex-col">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Your Experience Bullet Points</label>
                <textarea
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  placeholder="Paste your recent job bullets here..."
                  className="w-full flex-1 min-h-[150px] bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground resize-none"
                />
              </div>
            </div>

            <Button
              onClick={handleOptimise}
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-black font-bold py-7 rounded-2xl shadow-xl shadow-primary/20 transition-all hover:scale-[1.01] active:scale-95 group"
            >
              <div className="flex items-center gap-2">
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5 transition-transform group-hover:rotate-12" />
                )}
                <span className="uppercase tracking-widest text-sm">
                  {loading ? "Optimising..." : "✨ Optimise My LinkedIn"}
                </span>
              </div>
            </Button>
          </div>

          {/* Right Column - Output */}
          <div className="bg-card rounded-2xl border border-border shadow-lg p-6 flex flex-col h-full min-h-[600px] relative">
            {!loading && !result && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 rounded-full bg-primary/5 flex items-center justify-center mb-4">
                  <Linkedin className="w-8 h-8 text-primary/40" />
                </div>
                <p className="text-muted-foreground uppercase tracking-widest font-bold text-sm">
                  Your optimised LinkedIn profile will appear here
                </p>
              </div>
            )}

            {loading && (
              <div className="space-y-6 animate-pulse">
                <div className="space-y-3">
                  <div className="h-4 w-1/4 bg-muted rounded"></div>
                  <div className="h-10 w-full bg-muted rounded-xl"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 w-1/4 bg-muted rounded"></div>
                  <div className="h-32 w-full bg-muted rounded-xl"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 w-1/4 bg-muted rounded"></div>
                  <div className="h-48 w-full bg-muted rounded-xl"></div>
                </div>
              </div>
            )}

            {result && !loading && (
              <div className="flex flex-col h-full space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg">Optimised Results</h3>
                  <Button
                    variant="outline"
                    onClick={copyAll}
                    className="h-8 text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 gap-1.5"
                  >
                    {copiedAll ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedAll ? "Copied" : "📋 Copy All"}
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto pr-2 space-y-6 custom-scrollbar">
                  {result.HEADLINE && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Optimised Headline</span>
                        <button onClick={() => copyToClipboard(result.HEADLINE, setCopiedHeadline)} className="text-muted-foreground hover:text-primary transition-colors">
                          {copiedHeadline ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="bg-muted/30 border border-border rounded-xl p-4 text-sm text-foreground">
                        {result.HEADLINE}
                      </div>
                    </div>
                  )}

                  {result.ABOUT && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Optimised About Section</span>
                        <button onClick={() => copyToClipboard(result.ABOUT, setCopiedAbout)} className="text-muted-foreground hover:text-primary transition-colors">
                          {copiedAbout ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="bg-muted/30 border border-border rounded-xl p-4 text-sm text-foreground whitespace-pre-wrap">
                        {result.ABOUT}
                      </div>
                    </div>
                  )}

                  {result.EXPERIENCE && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Optimised Experience Bullets</span>
                        <button onClick={() => copyToClipboard(result.EXPERIENCE, setCopiedExperience)} className="text-muted-foreground hover:text-primary transition-colors">
                          {copiedExperience ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="bg-muted/30 border border-border rounded-xl p-4 text-sm text-foreground whitespace-pre-wrap">
                        {result.EXPERIENCE}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        ) : (
          /* Job Search Helper View */
          <div className="max-w-xl mx-auto bg-card border border-border shadow-lg rounded-2xl p-8 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Search className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-foreground">LinkedIn Job Search Helper</h3>
                <p className="text-muted-foreground text-xs">Construct customized search parameters to discover job postings within precise timelines.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Job Title / Keywords</label>
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="e.g. Frontend Developer, Facebook Ads"
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground placeholder:text-muted-foreground/50"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Location</label>
                <input
                  type="text"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  placeholder="e.g. London, Remote"
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground placeholder:text-muted-foreground/50"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Date Posted Time Frame</label>
                <select
                  value={timeFrame}
                  onChange={(e) => setTimeFrame(e.target.value)}
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground"
                >
                  <option value="r1800">Past 30 minutes</option>
                  <option value="r3600">Past 1 hour</option>
                  <option value="r21600">Past 6 hours</option>
                  <option value="r86400">Past 24 hours</option>
                  <option value="r259200">Past 3 days</option>
                </select>
              </div>
            </div>

            <Button
              onClick={handleSearchClick}
              className="w-full bg-primary hover:bg-primary/90 text-black font-bold py-6 rounded-xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              <span>Search Jobs on LinkedIn</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
